import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DataTablePagination } from '@/components/ui/data-table/DataTablePagination';
import React from 'react';
import DataTableNoResult from './DataTableNoResult';
import { cn } from '@/lib/utils';
import TableRowSkeleton from './TableRowSkeleton';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  slots?: {
    pagination?: {
      isSelectPageSize?: boolean;
    };
    cell?: {
      className?: string;
    };
  };
  isLoading?: boolean;
  isHidePagination?: boolean;
  onClickRow?: (row: Row<TData>) => void;
  onDoubleClickRow?: (row: Row<TData>) => void;
  defaultSorting?: SortingState;
  defaultPageSize?: number;
  persistPageSize?: boolean;
  pageSizeStorageKey?: string;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  slots,
  isLoading,
  isHidePagination,
  onClickRow,
  onDoubleClickRow,
  defaultSorting,
  defaultPageSize = 30,
  persistPageSize = false,
  pageSizeStorageKey = 'data-table-page-size',
}: DataTableProps<TData, TValue>) {
  // Load saved page size from localStorage if persistence is enabled
  const getSavedPageSize = (): number => {
    if (!persistPageSize) return defaultPageSize;
    try {
      const saved = localStorage.getItem(pageSizeStorageKey);
      return saved ? parseInt(saved, 10) : defaultPageSize;
    } catch (error) {
      console.error('Error loading page size from localStorage:', error);
      return defaultPageSize;
    }
  };

  const [sorting, setSorting] = React.useState<SortingState>(defaultSorting || []);
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: getSavedPageSize(),
  });

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: isHidePagination
      ? undefined
      : getPaginationRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      pagination,
    },
  });

  // Save page size to localStorage whenever it changes
  React.useEffect(() => {
    if (!persistPageSize) return;
    try {
      localStorage.setItem(pageSizeStorageKey, pagination.pageSize.toString());
    } catch (error) {
      console.error('Error saving page size to localStorage:', error);
    }
  }, [pagination.pageSize, persistPageSize, pageSizeStorageKey]);

  return (
    <div>
      <div className="rounded-md">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      style={{
                        minWidth: header.column.columnDef.size,
                        maxWidth: header.column.columnDef.size,
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <>
                <TableRowSkeleton col={columns.length} row={3} />
              </>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={() => onClickRow && onClickRow(row)}
                  onDoubleClick={() => onDoubleClickRow && onDoubleClickRow(row)}
                  className={cn(onClickRow && 'cursor-pointer')}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      style={{
                        minWidth: cell.column.columnDef.size,
                        maxWidth: cell.column.columnDef.size,
                      }}
                      className={slots?.cell?.className}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <DataTableNoResult colSpan={columns.length} />
            )}
          </TableBody>
        </Table>
      </div>
      <div className="py-4">
        {!!table.getRowModel().rows?.length && !isHidePagination && (
          <DataTablePagination
            table={table}
            isSelectPageSize={slots?.pagination?.isSelectPageSize}
          />
        )}
      </div>
    </div>
  );
}
